import { create } from 'zustand';
import { errorMessage } from '../lib/errors.js';
import {
  type Message,
  listMessages,
  getMessage,
  setMessageRead,
  markAllMessagesRead,
  deleteMessage,
} from '../api/messages.js';

interface MessagesState {
  items: Message[];
  cursor: string | null;
  unread: number;
  loading: boolean;
  loadingMore: boolean;
  error: string | null;
  load: () => Promise<void>;
  loadMore: () => Promise<void>;
  fetchOne: (messageId: string) => Promise<Message>;
  markRead: (messageId: string, read: boolean) => Promise<void>;
  markAllRead: () => Promise<void>;
  remove: (messageId: string) => Promise<void>;
}

/**
 * Le load() en vol, s'il y en a un. Une notification tapée monte l'app et la vue détail en
 * même temps : sans ce rendez-vous, le GET /messages (compté avant le PATCH) revient après le
 * décrément optimiste et remet le badge à sa valeur d'avant lecture.
 */
let pendingLoad: Promise<void> | null = null;

export const useMessages = create<MessagesState>((set, get) => ({
  items: [],
  cursor: null,
  unread: 0,
  loading: false,
  loadingMore: false,
  error: null,

  load: async () => {
    set({ loading: true, error: null });
    const run = (async () => {
      try {
        const page = await listMessages();
        set({ items: page.items, cursor: page.cursor, unread: page.unread, loading: false });
      } catch (err) {
        set({ error: errorMessage(err), loading: false });
      }
    })();
    pendingLoad = run;
    await run;
    if (pendingLoad === run) pendingLoad = null;
  },

  loadMore: async () => {
    const { cursor, loadingMore } = get();
    if (!cursor || loadingMore) return;
    set({ loadingMore: true });
    try {
      const page = await listMessages(cursor);
      set({
        items: [...get().items, ...page.items],
        cursor: page.cursor,
        unread: page.unread,
        loadingMore: false,
      });
    } catch (err) {
      set({ error: errorMessage(err), loadingMore: false });
    }
  },

  // The detail view can be opened straight from a notification, before any list was fetched.
  fetchOne: async (messageId) => {
    const known = get().items.find((m) => m.messageId === messageId);
    if (known) return known;
    return getMessage(messageId);
  },

  markRead: async (messageId, read) => {
    if (pendingLoad) await pendingLoad;
    const before = get().items.find((m) => m.messageId === messageId);
    if (before && before.read === read) return;
    // The badge is what the user watches; move it now and reconcile on the next load().
    set({
      items: get().items.map((m) => (m.messageId === messageId ? { ...m, read } : m)),
      unread: Math.max(0, get().unread + (read ? -1 : 1)),
    });
    try {
      await setMessageRead(messageId, read);
    } catch (err) {
      set({ error: errorMessage(err) });
      await get().load();
    }
  },

  markAllRead: async () => {
    if (pendingLoad) await pendingLoad;
    const previous = get().items;
    set({ items: previous.map((m) => ({ ...m, read: true })), unread: 0 });
    try {
      await markAllMessagesRead();
    } catch (err) {
      set({ error: errorMessage(err), items: previous });
      await get().load();
    }
  },

  remove: async (messageId) => {
    if (pendingLoad) await pendingLoad;
    const gone = get().items.find((m) => m.messageId === messageId);
    set({
      items: get().items.filter((m) => m.messageId !== messageId),
      unread: gone && !gone.read ? Math.max(0, get().unread - 1) : get().unread,
    });
    try {
      await deleteMessage(messageId);
    } catch (err) {
      set({ error: errorMessage(err) });
      await get().load();
    }
  },
}));
